import { type IDPConsentSection, IdentityProvider } from "@stytch/react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { getDefaultStore } from "jotai";

import { returnToAtom } from "@/lib/atoms/return-to-atom";

const strings = {
  "idpConsent.customScopes.title":
    "{clientName} está solicitando acesso a estas permissões:",
  "idpConsent.button.allow": "Permitir",
  "idpConsent.button.deny": "Negar",
  "success.title": "Sucesso!",
  "login.success.content": "Login realizado. Redirecionando...",
};

const isProfileScope = (scope: string) =>
  scope === "openid" || scope === "email" || scope === "profile";

const isDataScope = (scope: string) => !isProfileScope(scope);

const getScopeDescription = (scope: string): string => {
  switch (scope) {
    case "openid":
      return "Confirmar quem você é (autenticação)";
    case "email":
      return "Seu endereço de e-mail";
    case "profile":
      return "Seu nome e foto de perfil";
    case "full_access":
      return "Acesso completo à sua conta";
    case "offline_access":
      return "Manter o acesso mesmo quando você não estiver usando o sistema";
    case "api.all":
      return "Realizar qualquer ação disponível na plataforma em seu nome";
    default:
      return scope;
  }
};

const getScopeConsequence = (scope: string): string => {
  switch (scope) {
    case "openid":
      return "Permite verificar sua identidade de forma segura, sem expor sua senha.";
    case "email":
      return "Usado para identificá-lo e enviar notificações importantes relacionadas ao seu atendimento.";
    case "profile":
      return "Permite exibir seu nome e foto dentro da plataforma.";
    case "full_access":
      return "O aplicativo poderá ler e modificar todos os dados da sua conta, incluindo agendamentos e configurações.";
    case "offline_access":
      return "O aplicativo poderá agir em seu nome mesmo quando você não estiver com a sessão ativa.";
    case "api.all":
      return "O aplicativo terá as mesmas permissões que você possui na plataforma, podendo realizar ações como agendar, cancelar e acessar informações de pacientes.";
    default:
      return `Concede a permissão "${scope}" ao aplicativo.`;
  }
};

const getManifest = ({
  scopes,
  clientName,
}: {
  scopes: string[];
  clientName: string;
}): IDPConsentSection[] => [
  {
    header: `${clientName} deseja confirmar sua identidade e acessar seu perfil`,
    items: scopes.filter(isProfileScope).map((scope) => ({
      text: getScopeDescription(scope),
      details: [getScopeConsequence(scope)],
    })),
  },
  {
    header: `${clientName} deseja operar em seu nome na plataforma`,
    items: scopes.filter(isDataScope).map((scope) => ({
      text: getScopeDescription(scope),
      details: [getScopeConsequence(scope)],
    })),
  },
];

const Authorize = () => {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <IdentityProvider strings={strings} getIDPConsentManifest={getManifest} />
    </div>
  );
};

export const Route = createFileRoute("/auth/oauth/authorize")({
  beforeLoad: async ({ context, location }) => {
    const hasValidSession =
      await context.stytchAuthenticationClient.hasValidServerSession();

    if (!hasValidSession) {
      const store = getDefaultStore();
      const returnTo = location.pathname + location.searchStr + location.hash;

      store.set(returnToAtom, returnTo);

      redirect({ to: "/auth/signin", throw: true });
    }
  },
  component: Authorize,
});
